import { checkTransactionLimit, TransactionLimit } from "../../Utility/Utility";
import { derivePaymentMode, isRedemptionFeeRequired } from "../utils/paymentModeUtils";

export const calculateCartPricing = (updatedData, productMapping) => {
  const convenienceFeePercent = Number(productMapping?.convenienceFee) || 0;
  const gstPercent = Number(productMapping?.gst) || 0;
  const customerDiscount = Number(productMapping?.customerDiscount) || 0;

  const totals = (updatedData || []).reduce(
    (result, item) => {
      const quantity = Number(item?.quantity) || 0;
      const denomination = Number(item?.denomination) || 0;
      const payable = Number(item?.you_pay) || 0;
      const convenienceFee =
        (payable * convenienceFeePercent * quantity) / 100;

      result.totalDenomination += denomination * quantity;
      result.totalQuantity += quantity;
      result.convenienceFeeAmount += convenienceFee;
      result.gstAmount += (convenienceFee * gstPercent) / 100;
      return result;
    },
    {
      totalDenomination: 0,
      totalQuantity: 0,
      convenienceFeeAmount: 0,
      gstAmount: 0,
    },
  );

  return {
    ...totals,
    totalDiscount: (totals.totalDenomination * customerDiscount) / 100,
    convenienceFeePercent,
    gstPercent,
    customerDiscount,
  };
};

export const calculateGrossPayableAmount = (
  updatedData,
  convenienceFee,
  gst,
) => {
  const basePayableAmount = (updatedData || []).reduce(
    (total, item) =>
      total + Number(item?.you_pay || 0) * Number(item?.quantity || 0),
    0,
  );

  return Number(
    (
      basePayableAmount +
      Number(convenienceFee || 0) +
      Number(gst || 0)
    ).toFixed(2),
  );
};

export function useCartPricing({
  totalPayableAmount,
  burnPoints,
  updatedData,
  quantities,
  convenienceFee,
  gst,
  productData,
  redemptionFeeApplied,
  MatchedCardMasterData,
}) {
  const transactionLimit = TransactionLimit();

  const { onlyCash, cashPlusPoints, onlyPoints, paymentMode } =
    derivePaymentMode(totalPayableAmount, burnPoints);

  const totalQuantity = (quantities ?? []).reduce((sum, v) => sum + v, 0);

  const totalDenominationAmount = (updatedData ?? []).reduce(
    (acc, item) =>
      acc + Number(item?.denomination || 0) * Number(item?.quantity || 0),
    0,
  );

  const totalDenominationAfterDiscountAmount = (updatedData ?? []).reduce(
    (acc, item) =>
      acc + Number(item?.you_pay || 0) * Number(item?.quantity || 0),
    0,
  );

  const payableAmount =
    totalDenominationAfterDiscountAmount +
    Number(convenienceFee || 0) +
    Number(gst || 0);

  const totalPaid = Number(
    (updatedData ?? []).reduce(
      (acc, card) => acc + (card?.you_pay || 0) * (card?.quantity || 0),
      0,
    ) +
      (convenienceFee || 0) +
      (gst || 0),
  );

  const { isTransactionLimitExceeded } = checkTransactionLimit({
    transactionAmountType: transactionLimit?.transactionAmountType,
    totalAmountCapping: transactionLimit?.totalAmountCapping,
    totalDenominationAmount,
    totalDenominationAfterDiscountAmount,
    payableAmount,
  });

  const earnMinDeno = productData?.minEarnPoints;
  const burmMinDeno = productData?.maxEarnPoints;

  const clientCommissionData = (() => {
    const c = productData?.clientCommission;
    return c === null || c === undefined || c === "" || !c ? 0 : c;
  })();

  const isRedemptionRequired = isRedemptionFeeRequired(
    redemptionFeeApplied,
    paymentMode,
  );

  const cardRedemptionFeeEnabled =
    MatchedCardMasterData?.redemptionFeeEnabled !== false;

  const showRedemptionFee = isRedemptionRequired && cardRedemptionFeeEnabled;

  return {
    onlyCash,
    cashPlusPoints,
    onlyPoints,
    paymentMode,
    totalQuantity,
    totalDenominationAmount,
    totalDenominationAfterDiscountAmount,
    payableAmount,
    totalPaid,
    isTransactionLimitExceeded,
    earnMinDeno,
    burmMinDeno,
    clientCommissionData,
    isRedemptionRequired,
    showRedemptionFee,
  };
}
